import { memo, useMemo } from 'react';
import {
  Container,
  Grid,
  Typography,
  CircularProgress,
  Button,
} from '@mui/material';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import { environment } from 'apps/table-tuck-web/src/environments/environment';
import basket from '../../../../assets/empty_bag.png';
import CartItemRow from './CartItemRow';

/**
 * Cart items list with loading and empty states
 * Memoized to prevent unnecessary re-renders
 */
const CartItemsList = memo(
  ({
    apiPricingData,
    cartLoading,
    pricingError,
    orderType,
    onAddItems,
    cart,
  }) => {
    const lineItems = useMemo(
      () => apiPricingData?.lineItemData || [],
      [apiPricingData]
    );

    // Loading state - show when actively loading OR when we have cart items but no pricing data yet
    // This prevents the empty state from flashing before the loading state appears
    const hasError = !!pricingError;
    const shouldShowLoading =
      !hasError &&
      (cartLoading ||
        (Array.isArray(cart) && cart.length > 0 && apiPricingData === null));

    // Error state: cart has items but pricing failed to load
    if (
      hasError &&
      Array.isArray(cart) &&
      cart.length > 0 &&
      apiPricingData === null
    ) {
      return (
        <Container
          maxWidth="lg"
          sx={{
            height: '70vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <Grid
            container
            style={{
              display: 'flex',
              justifyContent: 'center',
              paddingBottom: '30px',
              textAlign: 'center',
            }}
          >
            <Grid
              item
              lg={12}
              style={{ display: 'flex', justifyContent: 'center' }}
            >
              <ShoppingCartOutlinedIcon
                sx={{ fontSize: 80, color: environment.mainColor }}
              />
            </Grid>
          </Grid>
          <Grid
            container
            style={{
              display: 'flex',
              justifyContent: 'center',
              paddingBottom: '16px',
            }}
          >
            <div>
              <Typography
                variant="h6"
                style={{ fontWeight: 'bold', textAlign: 'center' }}
              >
                We&apos;re having trouble loading your cart
              </Typography>
              <br />
              <Typography
                variant="body2"
                style={{
                  fontWeight: 500,
                  color: 'grey',
                  maxWidth: 360,
                  textAlign: 'center',
                }}
              >
                Please check your connection and try again in a moment. Your
                items are still safe in your cart.
              </Typography>
            </div>
          </Grid>
        </Container>
      );
    }

    if (shouldShowLoading) {
      return (
        <Container
          maxWidth="lg"
          sx={{
            height: '60vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <CircularProgress size={40} sx={{ color: environment.mainColor }} />
          <Typography
            variant="body1"
            sx={{
              fontFamily: 'Jost, sans-serif',
              color: '#666',
              textAlign: 'center',
            }}
          >
            Loading your cart...
          </Typography>
        </Container>
      );
    }

    // Empty cart state
    if (!lineItems.length) {
      return (
        <Container
          maxWidth="lg"
          sx={{
            height: '80vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <Grid
            container
            style={{
              display: 'flex',
              justifyContent: 'center',
              paddingBottom: '30px',
              textAlign: 'center',
            }}
          >
            <Grid
              item
              lg={12}
              style={{ display: 'flex', justifyContent: 'center' }}
            >
              <img src={basket} alt="Empty cart" />
            </Grid>
          </Grid>
          <Grid
            container
            style={{
              display: 'flex',
              justifyContent: 'center',
              paddingBottom: '30px',
            }}
          >
            <div>
              <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                Your cart is empty
              </Typography>
              <br />
              <Typography
                variant="body2"
                style={{ fontWeight: 'bold', color: 'grey' }}
              >
                Add items to get started
              </Typography>
            </div>
          </Grid>
          <Grid
            container
            style={{
              display: 'flex',
              justifyContent: 'center',
              textAlign: 'center',
            }}
          >
            <Button
              variant="contained"
              disableRipple
              sx={{
                textTransform: 'capitalize',
                padding: '16px 40px',
                width: '320px',
                boxShadow: 'none',
                fontWeight: 600,
                borderRadius: '43px',
                backgroundColor: environment.mainColor,
                fontFamily: 'Jost',
                '&:hover': {
                  backgroundColor: environment.mainColor,
                  boxShadow: 'none',
                },
              }}
              onClick={onAddItems}
            >
              Add Items
            </Button>
          </Grid>
        </Container>
      );
    }

    // Cart items
    return (
      <div style={{ paddingTop: '20px', cursor: 'default' }}>
        {lineItems.map((lineItem, index) => (
          <CartItemRow
            key={`${lineItem.variationId || 'item'}-${index}`}
            lineItem={lineItem}
            index={index}
            orderType={orderType}
          />
        ))}
      </div>
    );
  }
);

CartItemsList.displayName = 'CartItemsList';

export default CartItemsList;
